import React, { useState } from 'react';
import {
  CircleCheck,
  Copy,
  ExternalLink,
  Eye,
  Hide,
  InfoCircle,
  Link,
  ShieldCheck,
} from '@saleshandy/icons';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';
import Button from '../../../../../../shared/design-system/components/atoms/button';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { MCP_TOOLS, GLOBAL_MCP_URL, ToolConfig } from '../mcp-tools-config';

type Step = 'tool-selection' | 'connect';

type Props = {
  show: boolean;
  onClose: () => void;
  generatedApiToken: string;
  createApiKeyRequestStatus: RequestStatus;
  mcpApiKeyExists: boolean;
  sendCreateMcpApiKeyRequest: () => void;
  sendFetchMcpApiKeyRequest: () => void;
  resetCreateMcpApiKeyRequest: () => void;
};

const MASKED_KEY_PLACEHOLDER = '••••••••••••••••••••••••';

const McpSetupModal: React.FC<Props> = ({
  show,
  onClose,
  generatedApiToken,
  createApiKeyRequestStatus,
  mcpApiKeyExists,
  sendCreateMcpApiKeyRequest,
  sendFetchMcpApiKeyRequest,
  resetCreateMcpApiKeyRequest,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('tool-selection');
  const [selectedTool, setSelectedTool] = useState<ToolConfig | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const generatedUrl = generatedApiToken
    ? `${GLOBAL_MCP_URL}/${generatedApiToken}`
    : '';

  const maskedUrl = `${GLOBAL_MCP_URL}/${MASKED_KEY_PLACEHOLDER}`;

  const displayUrl = generatedApiToken
    ? isKeyVisible
      ? generatedUrl
      : maskedUrl
    : `${GLOBAL_MCP_URL}/YOUR_API_KEY`;

  const handleToolSelect = (tool: ToolConfig) => {
    setSelectedTool(tool);
    setCurrentStep('connect');
    if (tool.authType === 'api-key') {
      sendFetchMcpApiKeyRequest();
    }
  };

  const resetState = () => {
    setCurrentStep('tool-selection');
    setSelectedTool(null);
    setCopiedId(null);
    setIsKeyVisible(false);
    setShowRegenerateConfirm(false);
    resetCreateMcpApiKeyRequest();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    resetState();
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleGenerateOrRegenerate = () => {
    if (hasApiKey || mcpApiKeyExists) {
      setShowRegenerateConfirm(true);
    } else {
      sendCreateMcpApiKeyRequest();
    }
  };

  const isApiKeyLoading = createApiKeyRequestStatus === RequestStatus.Pending;
  const hasApiKey = !!generatedApiToken;

  // Config JSON strings
  const httpConfigDisplay = `{
  "mcpServers": {
    "saleshandy": {
      "transport": "http",
      "url": "${displayUrl}"
    }
  }
}`;

  const httpConfigCopy = `{
  "mcpServers": {
    "saleshandy": {
      "transport": "http",
      "url": "${hasApiKey ? generatedUrl : `${GLOBAL_MCP_URL}/YOUR_API_KEY`}"
    }
  }
}`;

  const stdioDisplay = `"args": ["-y", "mcp-remote", "${displayUrl}"]`;

  const stdioConfigCopy = `"args": ["-y", "mcp-remote", "${
    hasApiKey ? generatedUrl : `${GLOBAL_MCP_URL}/YOUR_API_KEY`
  }"]`;

  const renderToolSelection = () => (
    <div className="mcp-setup-modal__tool-selection">
      <div className="mcp-setup-modal__section-header">
        <h4 className="mcp-setup-modal__section-title">
          Select your MCP client
        </h4>
      </div>
      <div className="mcp-setup-modal__tool-grid">
        {MCP_TOOLS.map((tool) => (
          <button
            key={tool.id}
            type="button"
            className="mcp-setup-modal__tool-card"
            onClick={() => handleToolSelect(tool)}
          >
            <span
              className={`mcp-setup-modal__auth-tag ${
                tool.authType === 'api-key'
                  ? 'mcp-setup-modal__auth-tag--api-key'
                  : 'mcp-setup-modal__auth-tag--oauth'
              }`}
            >
              {tool.authType === 'api-key' ? 'Connect with API' : 'OAuth 2.1'}
            </span>
            <div className={`mcp-setup-modal__tool-icon ${tool.iconBgClass}`}>
              <tool.icon />
            </div>
            <div className="mcp-setup-modal__tool-info">
              <p className="mcp-setup-modal__tool-name">{tool.name}</p>
              <p className="mcp-setup-modal__tool-desc">{tool.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderApiKeyFlow = () => (
    <div className="mcp-setup-modal__connect">
      <div className="mcp-setup-modal__platform-badge">
        <span className="mcp-setup-modal__platform-label">Platform:</span>
        <span className="mcp-setup-modal__platform-value">
          {selectedTool?.name}
        </span>
        <span className="mcp-setup-modal__api-badge">Connect with API</span>
      </div>

      {selectedTool?.prerequisites && (
        <div className="mcp-setup-modal__prerequisites">
          <InfoCircle
            width={14}
            height={14}
            className="mcp-setup-modal__prereq-icon"
          />
          <p>{selectedTool.prerequisites}</p>
        </div>
      )}

      <div className="mcp-setup-modal__instructions-box">
        <div className="mcp-setup-modal__api-key-info">
          <InfoCircle
            width={14}
            height={14}
            className="mcp-setup-modal__api-key-info-icon"
          />
          <p>
            Configure any MCP-compatible client using your Saleshandy API key.
          </p>
        </div>

        <div className="mcp-setup-modal__api-steps">
          {/* Step 1: Generate URL */}
          <div className="mcp-setup-modal__api-step">
            <div className="mcp-setup-modal__api-step-header">
              <span className="mcp-setup-modal__step-number">1</span>
              <p className="mcp-setup-modal__api-step-title">
                Generate Ready-to-use URL
              </p>
              <Button
                variant={Button.Variant.Primary}
                className="mcp-setup-modal__generate-btn"
                onClick={handleGenerateOrRegenerate}
                isLoading={isApiKeyLoading}
                disabled={isApiKeyLoading}
              >
                {hasApiKey || mcpApiKeyExists ? 'Regenerate' : 'Generate URL'}
              </Button>
            </div>
            <div className="mcp-setup-modal__api-step-content">
              {hasApiKey ? (
                <div className="mcp-setup-modal__generated-section">
                  <div className="mcp-setup-modal__generated-url">
                    <div className="mcp-setup-modal__url-with-toggle">
                      <span className="mcp-setup-modal__url-text">
                        {isKeyVisible ? generatedUrl : maskedUrl}
                      </span>
                      <button
                        type="button"
                        className="mcp-setup-modal__icon-btn"
                        onClick={() => setIsKeyVisible(!isKeyVisible)}
                        title={isKeyVisible ? 'Hide Key' : 'Show Key'}
                      >
                        {isKeyVisible ? (
                          <Hide width={12} height={12} />
                        ) : (
                          <Eye width={12} height={12} />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      className={`mcp-setup-modal__url-copy-btn ${
                        copiedId === 'url' ? 'mcp-setup-modal__url-copy-btn--copied' : ''
                      }`}
                      onClick={() => {
                        copyText(generatedUrl, 'url');
                        setIsKeyVisible(false);
                      }}
                    >
                      {copiedId === 'url' ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p className="mcp-setup-modal__success-note">
                    API Key created as{' '}
                    <em>&quot;MCP - Custom Integration&quot;</em> in your API
                    Key page.
                  </p>
                </div>
              ) : (
                <p className="mcp-setup-modal__api-step-desc">
                  Click the button to automatically create an API key and
                  generate your unique MCP endpoint.
                </p>
              )}
            </div>
          </div>

          {/* Step 2: Configure Client */}
          <div className="mcp-setup-modal__api-step">
            <div className="mcp-setup-modal__api-step-header">
              <span className="mcp-setup-modal__step-number">2</span>
              <p className="mcp-setup-modal__api-step-title">
                Configure Your Client
              </p>
            </div>
            <div className="mcp-setup-modal__api-step-body">
              <p className="mcp-setup-modal__api-step-desc">
                Copy the URL from Step 1 and paste it into your tool&apos;s MCP
                configuration.
              </p>
            </div>
          </div>

          {/* Step 3: Configuration Examples */}
          <div className="mcp-setup-modal__api-step">
            <div className="mcp-setup-modal__api-step-header">
              <span className="mcp-setup-modal__step-number">3</span>
              <p className="mcp-setup-modal__api-step-title">
                Configuration Examples
              </p>
            </div>
            <div className="mcp-setup-modal__api-step-content">
              <div className="mcp-setup-modal__config-example">
                <span className="mcp-setup-modal__config-label">
                  Standard HTTP (Recommended)
                </span>
                <div className="mcp-setup-modal__config-block-wrapper">
                  <pre className="mcp-setup-modal__config-block">
                    <code>{httpConfigDisplay}</code>
                  </pre>
                  <button
                    type="button"
                    className={`mcp-setup-modal__config-copy-btn ${
                      copiedId === 'http' ? 'mcp-setup-modal__config-copy-btn--copied' : ''
                    }`}
                    onClick={() => copyText(httpConfigCopy, 'http')}
                    title="Copy JSON"
                  >
                    {copiedId === 'http' ? (
                      <CircleCheck width={12} height={12} />
                    ) : (
                      <Copy width={12} height={12} />
                    )}
                  </button>
                </div>
              </div>

              <div className="mcp-setup-modal__config-example">
                <span className="mcp-setup-modal__config-label">
                  Stdio Transport (via mcp-remote)
                </span>
                <div className="mcp-setup-modal__config-block-wrapper">
                  <pre className="mcp-setup-modal__config-block">
                    <code>{stdioDisplay}</code>
                  </pre>
                  <button
                    type="button"
                    className={`mcp-setup-modal__config-copy-btn ${
                      copiedId === 'stdio' ? 'mcp-setup-modal__config-copy-btn--copied' : ''
                    }`}
                    onClick={() => copyText(stdioConfigCopy, 'stdio')}
                    title="Copy"
                  >
                    {copiedId === 'stdio' ? (
                      <CircleCheck width={12} height={12} />
                    ) : (
                      <Copy width={12} height={12} />
                    )}
                  </button>
                </div>
                <p className="mcp-setup-modal__config-hint">
                  Use this if your client doesn&apos;t natively support HTTP
                  transport.
                </p>
              </div>
            </div>
          </div>

          {/* Step 4: Verify */}
          <div className="mcp-setup-modal__api-step">
            <div className="mcp-setup-modal__api-step-header">
              <span className="mcp-setup-modal__step-number">4</span>
              <p className="mcp-setup-modal__api-step-title">
                Verify Connection
              </p>
            </div>
            <div className="mcp-setup-modal__api-step-body">
              <p className="mcp-setup-modal__api-step-desc">
                Ask your AI:{' '}
                <strong>
                  <em>&quot;List my active sequences in Saleshandy&quot;</em>
                </strong>
              </p>
              <p className="mcp-setup-modal__verify-success">
                <CircleCheck width={12} height={12} />
                Connected successfully if sequences are listed.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mcp-setup-modal__security-note">
        <ShieldCheck
          width={14}
          height={14}
          className="mcp-setup-modal__security-icon"
        />
        <p>
          This connection uses API Key authentication. The generated key is
          automatically added to your API Keys list.
        </p>
      </div>

      {/* Regenerate Confirmation Modal */}
      <Modal
        show={showRegenerateConfirm}
        className="mcp-regenerate-modal"
        titleContent="Renew MCP URL"
        showCloseIcon={true}
        onClose={() => setShowRegenerateConfirm(false)}
        onHide={() => setShowRegenerateConfirm(false)}
        cancelButtonText="Cancel"
        onCancel={() => setShowRegenerateConfirm(false)}
        submitButtonText="Renew"
        onSubmit={() => {
          setShowRegenerateConfirm(false);
          sendCreateMcpApiKeyRequest();
        }}
      >
        <div className="mcp-regenerate-modal__body">
          <p>
            Are you sure you want to renew your MCP URL? This will permanently
            delete the existing API key{' '}
            <strong>&quot;MCP - Custom Integration&quot;</strong>. Any tool
            currently connected via the old URL will stop working immediately.
            This action is not reversible.
          </p>
        </div>
      </Modal>
    </div>
  );

  const renderConnectInstructions = () => (
    <div className="mcp-setup-modal__connect">
      <div className="mcp-setup-modal__platform-badge">
        <span className="mcp-setup-modal__platform-label">Platform:</span>
        <span className="mcp-setup-modal__platform-value">
          {selectedTool?.name}
        </span>
      </div>

      {selectedTool?.prerequisites && (
        <div className="mcp-setup-modal__prerequisites">
          <InfoCircle
            width={14}
            height={14}
            className="mcp-setup-modal__prereq-icon"
          />
          <p>{selectedTool.prerequisites}</p>
        </div>
      )}

      <div className="mcp-setup-modal__instructions-box">
        <div className="mcp-setup-modal__step-section">
          <h5 className="mcp-setup-modal__step-heading">
            <span className="mcp-setup-modal__step-number">1</span>
            Copy Endpoint URL
          </h5>
          <div className="mcp-setup-modal__url-row">
            <span className="mcp-setup-modal__url-text">{GLOBAL_MCP_URL}</span>
            <Button
              variant={Button.Variant.Primary}
              onClick={() => copyText(GLOBAL_MCP_URL, 'oauth-url')}
              className={`mcp-setup-modal__copy-btn ${
                copiedId === 'oauth-url' ? 'mcp-setup-modal__copy-btn--copied' : ''
              }`}
            >
              <Copy width={14} height={14} />
              {copiedId === 'oauth-url' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="mcp-setup-modal__step-section">
          <h5 className="mcp-setup-modal__step-heading">
            <span className="mcp-setup-modal__step-number">2</span>
            Instructions
          </h5>
          <div className="mcp-setup-modal__steps-list">
            {selectedTool?.steps.map((step, i) => (
              <div key={i} className="mcp-setup-modal__step-item">
                <span className="mcp-setup-modal__step-dot" />
                <span>{step}</span>
              </div>
            ))}
          </div>

          {selectedTool?.externalUrl && (
            <div className="mcp-setup-modal__external-link-wrapper">
              <a
                href={selectedTool.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mcp-setup-modal__external-link"
              >
                <ExternalLink width={13} height={13} />
                Go to {selectedTool.name} Settings
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="mcp-setup-modal__security-note">
        <ShieldCheck
          width={14}
          height={14}
          className="mcp-setup-modal__security-icon"
        />
        <p>
          This connection uses secure OAuth 2.1. Authorization happens directly
          with Saleshandy.
        </p>
      </div>
    </div>
  );

  const isApiKeyFlow = selectedTool?.authType === 'api-key';

  return (
    <Modal
      show={show}
      className="mcp-setup-modal"
      titleContent={
        <div className="mcp-setup-modal__header-content">
          <div className="mcp-setup-modal__header-icon">
            <Link width={16} height={16} />
          </div>
          <div>
            <h3 className="mcp-setup-modal__header-title">
              Set Up MCP Connection
            </h3>
            <p className="mcp-setup-modal__header-step">
              Step {currentStep === 'tool-selection' ? '1' : '2'} of 2
            </p>
          </div>
        </div>
      }
      showCloseIcon={true}
      onClose={handleClose}
      onHide={handleClose}
      hideFooter={currentStep === 'tool-selection'}
      cancelButtonText={currentStep === 'connect' ? 'Back' : 'Cancel'}
      onCancel={currentStep === 'connect' ? handleBack : handleClose}
      submitButtonText="Done"
      onSubmit={handleClose}
    >
      {currentStep === 'tool-selection'
        ? renderToolSelection()
        : isApiKeyFlow
        ? renderApiKeyFlow()
        : renderConnectInstructions()}
    </Modal>
  );
};

export default McpSetupModal;
